import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, MapPin, Phone } from 'lucide-react';
import type { DirectoryOrgDetail } from '@his/shared';
import { publicGet } from '@/lib/public-api';
import { Badge, TableSkeleton } from '@/components/ui/primitives';
import { EmptyState, NotFoundIllustration } from '@/components/ui/layout';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * One organisation, and the branches it has published.
 *
 * This is the step the whole directory turns on: a hospital group is a single
 * result, and choosing *which* of its branches you actually want happens here,
 * where the addresses and opening hours are.
 */
export function OrgPage() {
  const { slug = '' } = useParams();

  const org = useQuery({
    queryKey: ['public-org', slug],
    queryFn: () => publicGet<DirectoryOrgDetail>(`/facilities/${slug}`),
  });

  if (org.isLoading) {
    return (
      <TableSkeleton />
    );
  }

  if (org.isError || !org.data) {
    return (
      <EmptyState
        illustration={<NotFoundIllustration />}
        title="Not found"
        description="This facility is no longer listed."
      />
    );
  }

  return (
    <>
      <Link
        to="/find"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground focus-ring rounded-md"
      >
        <ArrowLeft className="h-4 w-4" /> All results
      </Link>

      <div className="mb-6 flex items-start gap-4">
        {org.data.logoUrl && (
          <img src={org.data.logoUrl} alt="" className="h-14 w-14 rounded-lg border object-contain" />
        )}
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{org.data.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{org.data.cities.join(' · ')}</p>
          {org.data.about && <p className="mt-2 max-w-2xl text-sm">{org.data.about}</p>}
        </div>
      </div>

      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {org.data.branchCount > 1 ? `${org.data.branchCount} branches` : 'Branch'}
      </h2>

      <div className="space-y-2">
        {org.data.branches.map((branch) => (
          <Link
            key={branch.branchId}
            to={`/find/${slug}/${branch.branchId}`}
            className="flex items-center justify-between gap-3 rounded-lg border bg-card p-4 transition-colors hover:border-primary focus-ring"
          >
            <div className="min-w-0">
              <p className="font-medium">{branch.branchName}</p>
              <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                {[branch.address, branch.city].filter(Boolean).join(', ')}
              </p>
              {branch.phone && (
                <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Phone className="h-3.5 w-3.5" />
                  {branch.phone}
                </p>
              )}

              <div className="mt-2 flex flex-wrap gap-1">
                {branch.types.map((type) => (
                  <Badge key={type} variant="secondary">
                    {type}
                  </Badge>
                ))}
                {branch.services.slice(0, 4).map((service) => (
                  <Badge key={service} variant="muted">
                    {service}
                  </Badge>
                ))}
              </div>

              {branch.openingHours.length > 0 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {branch.openingHours
                    .map((hours) => `${WEEKDAYS[hours.weekday]} ${hours.opensAt}–${hours.closesAt}`)
                    .join(' · ')}
                </p>
              )}
            </div>
            <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
          </Link>
        ))}
      </div>
    </>
  );
}
